import { EnvConfig, Media, MediaData } from '@gripp/shared-logic';
import { useMemo, useState } from 'react';
import { createTypedMediaItem, processTypedMediaItem } from './mediaUtils';

export const useTypedMedia = <T extends MediaData>(
  existingMedia: Media[] | undefined
) => {
  const baseMediaUrl = EnvConfig.mediaUrl;
  const baseApiUrl = EnvConfig.apiUrl;
  // Items picked in this session, held as the on-device file they were added as.
  // Re-deriving one from its server record swaps in a URL whose blob is not
  // written yet and starts a processing poll for a file the device already has —
  // and processing can rename the blob, so that URL may never appear at all.
  const [localMedia, setLocalMedia] = useState<T[]>([]);
  // `existingMedia` is a server read that can still report an id the user just
  // removed here, so removals are remembered rather than inferred from absence.
  const [removedIds, setRemovedIds] = useState<string[]>([]);
  // Local edits (upload progress, renames) applied on top of whichever item the
  // id resolves to, so they survive `existingMedia` re-emitting that record.
  const [overrides, setOverrides] = useState<Record<string, Partial<T>>>({});

  const typedMedia = useMemo(() => {
    const localById = new Map(localMedia.map((m) => [m.id, m]));
    const serverIds = new Set((existingMedia ?? []).map((m) => m.id));
    const removed = new Set(removedIds);
    const apply = (item: T): T => {
      const override = overrides[item.id];
      return override ? ({ ...item, ...override } as T) : item;
    };

    const fromServer = (existingMedia ?? [])
      .filter((m) => !removed.has(m.id))
      .map((m) =>
        apply(
          localById.get(m.id) ??
            createTypedMediaItem<T>(m, baseMediaUrl, baseApiUrl)
        )
      );

    const localOnly = localMedia
      .filter((m) => !serverIds.has(m.id) && !removed.has(m.id))
      .map(apply);

    return [...fromServer, ...localOnly];
  }, [existingMedia, localMedia, removedIds, overrides, baseMediaUrl, baseApiUrl]);

  const forget = (
    prevState: Record<string, Partial<T>>,
    mediaId: string
  ): Record<string, Partial<T>> => {
    if (!prevState[mediaId]) return prevState;
    const { [mediaId]: _removed, ...rest } = prevState;
    return rest as Record<string, Partial<T>>;
  };

  const handleAdd = (
    newMediaItem: T,
    callback?: (newMediaItem: Media) => void
  ) => {
    processTypedMediaItem(newMediaItem);
    callback?.(newMediaItem);
    setOverrides((prevState) => forget(prevState, newMediaItem.id));
    setRemovedIds((prevState) =>
      prevState.filter((id) => id !== newMediaItem.id)
    );
    setLocalMedia((prevState) => [...prevState, newMediaItem]);
  };

  const handleUpdate = (updatedMediaItem: T) => {
    setOverrides((prevState) => ({
      ...prevState,
      [updatedMediaItem.id]: {
        ...prevState[updatedMediaItem.id],
        ...updatedMediaItem,
      },
    }));
  };

  const handleDelete = (
    mediaId: string,
    callback?: (mediaId: string) => void
  ) => {
    callback?.(mediaId);
    setOverrides((prevState) => forget(prevState, mediaId));
    setRemovedIds((prevState) =>
      prevState.includes(mediaId) ? prevState : [...prevState, mediaId]
    );
    setLocalMedia((prevState) =>
      prevState.filter((media) => media.id !== mediaId)
    );
  };

  const handleUploadProgress = (mediaId: string, progress: number) => {
    const toSetProgress = {
      id: mediaId,
      uploadProgress: progress,
      isUploading: progress !== 1,
    } as T;
    handleUpdate(toSetProgress);
  };

  const reset = () => {
    setLocalMedia([]);
    setOverrides({});
    // Nothing else suppresses the server read, so clearing has to name the ids
    // it is clearing or they reappear on the next render.
    setRemovedIds((existingMedia ?? []).map((m) => m.id));
  };

  //todo: add error handling
  return {
    typedMedia,
    handleAdd,
    handleDelete,
    handleUploadProgress,
    handleUpdate,
    reset,
  };
};
